import type { ReactNode } from "react";

import type { BusbarEvidenceResponse } from "../services/busbarEvidence";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";
import { statusHeading } from "./BusbarEvidenceResultSummary";
import { SOURCE_LABELS } from "./StarterCoordinationResultSummary";
import {
  BUSBAR_KIND_LABELS,
  BUSBAR_MATERIAL_LABELS,
  CASE_LABELS,
  FAULT_TYPE_LABELS,
} from "./busbarEvidenceDraft";
import { VOLTAGE_DROP_BASIS_LABELS } from "./busbarSelectionDraft";

interface BusbarEvidenceResultPanelProps {
  result: BusbarEvidenceResponse;
  /** The engine that produced a stored run; names the engine behind a field it did not record. */
  engineVersion?: string;
}

// F1 (2026-10-15): a stored result of an earlier engine may lack a field the current contract
// knows (neutral.harmonic_data_available, neutral_review_required, trunking.voltage_drop_basis
// came with engine 0.2.0). Absent means "not recorded by the engine version that produced this
// run" and is said so - never shown as false, "not stated" or any value.
export function notRecordedText(engineVersion: string | undefined): string {
  return engineVersion === undefined
    ? "Not recorded (an earlier engine predates this field)"
    : `Not recorded (engine ${engineVersion} predates this field)`;
}

function NotRecorded({ field, engineVersion }: { field: string; engineVersion?: string }) {
  return <span data-not-recorded={field}>{notRecordedText(engineVersion)}</span>;
}

/** Carried by every result (A24 (g)): what the checks are and are not. */
export const EVIDENCE_NOTE =
  "Busbar ratings are builder-declared, design-verified evidence; this check does not prove " +
  "the busbar, and the PE bar is not evaluated (GAP-022).";

// The four words of the Result column. A check is true (passed), false (failed, a
// warning names it - or a review: a site ambient above the builder's reference, a fault
// duration the Icw duration does not cover, a rated voltage below the system voltage
// (IR-001 F1)) or null: not made, because no peak was held for the peak withstand check
// (A24 (d)-(f)).
export const RESULT_WORDS = {
  pass: "Pass",
  fail: "Fails",
  review: "Review",
  notEvaluated: "Not evaluated",
} as const;

type FalseMeaning = "fail" | "review";

function resultWord(check: boolean | null, whenFalse: FalseMeaning): string {
  if (check === null) {
    return RESULT_WORDS.notEvaluated;
  }
  return check ? RESULT_WORDS.pass : RESULT_WORDS[whenFalse];
}

function resultKey(check: boolean | null, whenFalse: FalseMeaning): string {
  if (check === null) {
    return "not-evaluated";
  }
  return check ? "pass" : whenFalse;
}

// One cell holding an engineering figure: four significant figures with the unit on
// screen, the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null; unit: string }) {
  const quantity = formatQuantity(value, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

// A rating with a figure the engine reported beside it: "Icw 50 kA · margin 14.78 kA",
// "Rated 1600 A · loading 78.13 %".
function RatingWith({
  name,
  rating,
  unit,
  besideName,
  beside,
  besideUnit,
  note,
}: {
  name: string;
  rating: string;
  unit: string;
  besideName: string;
  beside: string | null;
  besideUnit: string;
  // What else the engine echoed beside the rating, as words - or a field that engine did
  // not record, said so (F1).
  note?: ReactNode;
}) {
  const value = formatQuantity(rating, unit);
  const figure = formatQuantity(beside, besideUnit);
  return (
    <td title={value.exact ?? undefined}>
      {name} {value.display} · {besideName} {figure.display}
      {note === undefined ? null : <> · {note}</>}
    </td>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

// The fault level the busbar was held against, as the stored fault run holds it - or
// as typed, with no run, case or fault type (A24 (c)). ip is empty when it was not held.
function FaultLevelTable({ result }: { result: BusbarEvidenceResponse }) {
  const linked = result.current_source === "LINKED_FAULT_RUN";
  const current = formatQuantity(result.prospective_current_ka, "kA");
  const peak = formatQuantity(result.peak_current_ka, "kA");
  const voltage = formatQuantity(result.system_nominal_voltage_v, "V");

  return (
    <ValueTable title="Fault level">
      <tr data-row-key="current_source">
        <th scope="row">Fault level source</th>
        <td data-current-source={result.current_source}>{SOURCE_LABELS[result.current_source]}</td>
      </tr>
      <tr data-row-key="fault_run_id">
        <th scope="row">Fault run</th>
        {/* The stored run's id: the evidence the fault level was read from. */}
        <td>{result.fault_run_id ?? EMPTY_QUANTITY}</td>
      </tr>
      <tr data-row-key="fault_bus_code">
        <th scope="row">Fault bus</th>
        <td>{result.fault_bus_code}</td>
      </tr>
      <tr data-row-key="bus_code">
        <th scope="row">Study bus</th>
        <td>{result.bus_code}</td>
      </tr>
      <tr data-row-key="calculation_case">
        <th scope="row">Calculation case</th>
        <td>
          {linked && result.calculation_case
            ? CASE_LABELS[result.calculation_case]
            : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="fault_type">
        <th scope="row">Fault type</th>
        <td>
          {linked && result.fault_type ? FAULT_TYPE_LABELS[result.fault_type] : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="prospective_current_ka">
        <th scope="row">Prospective current Ik'' (kA)</th>
        <td title={current.exact ?? undefined}>{current.display}</td>
      </tr>
      <tr data-row-key="peak_current_ka">
        <th scope="row">Peak current ip (kA)</th>
        <td title={peak.exact ?? undefined}>{peak.display}</td>
      </tr>
      <tr data-row-key="system_nominal_voltage_v">
        <th scope="row">System nominal voltage (V)</th>
        <td title={voltage.exact ?? undefined}>{voltage.display}</td>
      </tr>
    </ValueTable>
  );
}

type Check = {
  key: string;
  label: string;
  value: ReactNode;
  limit: ReactNode;
  check: boolean | null;
  whenFalse: FalseMeaning;
  // The engine says the check is a review although its figures hold (the neutral below the
  // phase without harmonic data); a failed check stays a failure.
  review?: boolean;
};

function outcomeOf(item: Check): { key: string; word: string } {
  if (item.review === true && item.check !== false) {
    return { key: "review", word: RESULT_WORDS.review };
  }
  return { key: resultKey(item.check, item.whenFalse), word: resultWord(item.check, item.whenFalse) };
}

// Each check beside the figure it was held against: the design current against the
// builder's rated current, the site ambient against the builder's reference, Ik'' and
// its duration against Icw and its duration, ip against Ipk, the system voltage against
// Ue, then the neutral pair and the trunking voltage drop when the study carries them
// (A24 (d)-(f)). Every figure is the engine's; nothing is compared or recomputed here.
function ChecksTable({ result, engineVersion }: BusbarEvidenceResultPanelProps) {
  const checks: Check[] = [
    {
      key: "rated-current",
      label: "Rated current",
      value: <Quantity value={result.design_current_a} unit="A" />,
      limit: (
        <RatingWith
          name="Rated"
          rating={result.declared_rated_current_a}
          unit="A"
          besideName="loading"
          beside={result.loading_percent}
          besideUnit="%"
        />
      ),
      check: result.rated_current_adequate,
      whenFalse: "fail",
    },
    {
      key: "ambient",
      label: "Site ambient",
      value: <Quantity value={result.site_ambient_c} unit="°C" />,
      limit: <Quantity value={result.reference_ambient_c} unit="°C" />,
      check: result.ambient_within_reference,
      // Above the reference is a review: derating needs the builder's data (A24 (d)).
      whenFalse: "review",
    },
    {
      key: "icw",
      label: "Short-time withstand Icw",
      value: <Quantity value={result.prospective_current_ka} unit="kA" />,
      limit: (
        <RatingWith
          name="Icw"
          rating={result.short_time_withstand_current_ka}
          unit="kA"
          besideName="margin"
          beside={result.icw_margin_ka}
          besideUnit="kA"
        />
      ),
      check: result.icw_adequate,
      whenFalse: "fail",
    },
    {
      key: "duration",
      label: "Fault duration",
      value: <Quantity value={result.required_fault_duration_s} unit="s" />,
      limit: <Quantity value={result.short_time_withstand_duration_s} unit="s" />,
      check: result.duration_within_icw,
      // A longer fault than Icw is rated for is a review of the builder's I2t (A24 (e)).
      whenFalse: "review",
    },
    {
      key: "ipk",
      label: "Peak withstand Ipk",
      value: <Quantity value={result.peak_current_ka} unit="kA" />,
      limit: (
        <RatingWith
          name="Ipk"
          rating={result.peak_withstand_current_ka}
          unit="kA peak"
          besideName="margin"
          beside={result.ipk_margin_ka}
          besideUnit="kA"
        />
      ),
      // No peak held: the check was not made (A24 (e)).
      check: result.ipk_adequate,
      whenFalse: "fail",
    },
    {
      key: "voltage",
      label: "Rated voltage",
      value: <Quantity value={result.system_nominal_voltage_v} unit="V" />,
      limit: <Quantity value={result.rated_operational_voltage_v} unit="V" />,
      check: result.voltage_adequate,
      // Below the system voltage is a review, not a failure (IR-001 F1).
      whenFalse: "review",
    },
  ];
  if (result.neutral !== null) {
    checks.push({
      key: "neutral",
      label: "Neutral",
      value: <Quantity value={result.neutral.design_neutral_current_a} unit="A" />,
      limit: (
        <RatingWith
          name="Rating"
          rating={result.neutral.declared_neutral_rating_a}
          unit="A"
          besideName="loading"
          beside={result.neutral_loading_percent}
          besideUnit="%"
          note={
            <>
              {result.neutral.harmonic_data_available === undefined ? (
                <NotRecorded field="neutral.harmonic_data_available" engineVersion={engineVersion} />
              ) : result.neutral.harmonic_data_available === true ? (
                "harmonic data stated"
              ) : (
                "harmonic data not stated"
              )}
              {result.neutral_review_required === undefined ? (
                <>
                  {" · review: "}
                  <NotRecorded field="neutral_review_required" engineVersion={engineVersion} />
                </>
              ) : null}
            </>
          }
        />
      ),
      check: result.neutral_adequate,
      whenFalse: "fail",
      review: result.neutral_review_required === true,
    });
  }
  if (result.trunking !== null) {
    checks.push({
      key: "voltage-drop",
      label: "Voltage drop",
      value: <Quantity value={result.voltage_drop_percent} unit="%" />,
      limit: (
        <RatingWith
          name="Allowable"
          rating={result.trunking.allowable_voltage_drop_percent}
          unit="%"
          besideName="drop"
          beside={result.voltage_drop_v}
          besideUnit="V"
          note={
            result.trunking.voltage_drop_basis === undefined ? (
              <NotRecorded field="trunking.voltage_drop_basis" engineVersion={engineVersion} />
            ) : result.trunking.voltage_drop_basis === null ? (
              "basis not stated"
            ) : (
              VOLTAGE_DROP_BASIS_LABELS[result.trunking.voltage_drop_basis].toLowerCase()
            )
          }
        />
      ),
      check: result.voltage_drop_adequate,
      whenFalse: "fail",
    });
  }

  return (
    <section aria-label="Busbar checks">
      <h3>Busbar checks</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Check</th>
              <th scope="col">Value</th>
              <th scope="col">Limit or requirement</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((item) => {
              const outcome = outcomeOf(item);
              return (
                <tr key={item.key} data-check-key={item.key}>
                  <th scope="row">{item.label}</th>
                  {item.value}
                  {item.limit}
                  <td data-check-result={outcome.key}>
                    {/* The word in a pill (A25 (d)): the cell keeps the outcome attribute and
                        the text; the span is the styling hook of the pill alone. */}
                    <span data-check-pill>
                      <StatusIcon tone={checkTone(outcome.key)} size={16} />
                      {outcome.word}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* What else the study declared; none of it changes a check. */}
      <p data-busbar-echo="true">
        <span data-busbar-kind={result.kind}>{BUSBAR_KIND_LABELS[result.kind]}</span>
        {" · "}
        <span data-busbar-material={result.material}>
          {BUSBAR_MATERIAL_LABELS[result.material]}
        </span>
      </p>
    </section>
  );
}

export function BusbarEvidenceResultPanel({ result, engineVersion }: BusbarEvidenceResultPanelProps) {
  return (
    <article aria-label="Busbar evidence result">
      <header>
        <h2>Busbar evidence result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status:{" "}
          <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <FaultLevelTable result={result} />

      <ChecksTable result={result} engineVersion={engineVersion} />

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The checks themselves carry
          no profile-dependent value; the profile is recorded with the run and must match the
          design basis of the project revision.
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the linked fault study, every
          busbar rating and the builder's design verification before approval or issue.{" "}
          {EVIDENCE_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default BusbarEvidenceResultPanel;
